<template lang="pug">
    .pdp-entry-ssr
      PostColorAdvisorEmailCapture
      component(:is="pdpComponent")
</template>

<script>
  import afterpayMixin from '@mixins/afterpayMixin';
  import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
  import vueProductSvc from "@services/vueProductSvc";

  import PostColorAdvisorEmailCapture from "@components/PDP/ColorKitPdp/PostColorAdvisorEmailCapture/PostColorAdvisorEmailCapture.vue";
  import { addGtagDynamicRemarketingEvent } from '@utilities/eventTrackingUtils';

  const ColorKitPdpV2 = () => import(/* webpackChunkName: "color-kit-pdp-v2" */ './ColorKitPdpV2');

  export default {
    name: 'PdpEntrySsr',

    components: {
      ColorKitPdpV2,
      PostColorAdvisorEmailCapture
    },

    mixins: [afterpayMixin],
    inject: ['getObjProperty'],

    props: {
      urlKey: String,
      cmsSettings: {
        type: Object,
        default: () => ({})
      }
    },

    data() {
      return {
        productCategory: null,
        component: null,
        perfectPairEventSent: false,
        isMounted: false,
      };
    },

    computed: {
      ...mapState('pdp', [
        'product',
        'subscription'
      ]),

      ...mapGetters('pdp', [
        'hasActiveSub',
        'showPerfectPairOnPdp',
        'perfectPairGloss'
      ]),
      
      ...mapState('customer', ['cdata']),

      pdpComponent() {
        let component = null;

        switch (this.product.product_type) {
          case 'color_kit':
            component = ColorKitPdpV2;
            break;
        }

        return component;
      }
    },

    watch: {
      urlKey() {
        this.initProduct();
      },

      /* global dataLayer */
      product(val) {
        if (val && this.cdata) {
          if (val.product_type && val.product_type != this.productCategory) {
            this.productCategory = val.product_type;

            this.trackEvent('Viewed Product Category', {
              category: val.product_type,
            });
          }

          this.trackEvent('Viewed Product', {
            id: val.id,
            sku: val.code,
            name: val.name,
            price: val.price,
            subPrice: val.sub_price,
            category: val.productType,
          });

          var gtmEvent = {
            'prodId': val.id
          };

          var eventNameMap = {
            'root_touch_up': 'RTUPageView',
            'haircare': 'RTUPageView',
            'gloss': 'RTUPageView',
            'styling': 'RTUPageView',
            'color_kit': 'RTUPageView',
            'accessory': 'RTUPageView',
          };

          gtmEvent['event'] = eventNameMap[val.product_type] || 'PageView';

          if (val.productColor) {
            gtmEvent['prodColor'] = val.productColor;
          }

          dataLayer.push(gtmEvent);

          addGtagDynamicRemarketingEvent(
            'view_item',
            val.price,
            [{
              'id': val.id,
              'google_business_vertical': 'retail'
            }]
          );

          if (window.DY) {
            if (val.id != window.DY?.recommendationContext?.data?.[0]) {
              window.DY.recommendationContext = { type:'PRODUCT', data: ['' + val.id] };
            }
          }
          this.loadDYCrossSell(val);
        }
      },

      showPerfectPairOnPdp(val) {
        if (val && !this.perfectPairEventSent) {
          let eventPayload = {
            color_kit_code: this.product.code,
            gloss_code: this.perfectPairGloss.code
          };

          this.trackMREvent('Perfect Pair Eligible', eventPayload);
          this.perfectPairEventSent = true;
        }
      }
    },

    mounted() {
      
      this.addProductToRecentlyViewed();
      if (location.hash) {
        this.scrollTo(location.hash, 400, 750);
      }

      this.mix_loadAfterPayScript();
      this.priceModuleDisplayLogic();
      this.isMounted = true;
      // initProduct MUST BE duplicated to update product data based on user session
      this.$nextTick(() => {
        this.initProduct().then(() => {
          // This block ensures promise to run properly.
        });
      });
    },

    async serverPrefetch() {
      try {
        await this.initProductSsr();
      } catch (error) {
        return null;
      }
    },

    destroyed() {
      this.resetBreadcrumbs();
    },

    methods: {
      ...mapActions('pdp', [
        'getProductByUrlKey',
        'getCustomerNextSubByProductType',
      ]),

      ...mapMutations('pdp', ['setShowPriceModule', 'setGoogleShopping', 'setDYRecommendedProducts']),
      ...mapMutations('cms', ['setCmsSettings']),
      ...mapMutations('global', ['setBreadcrumbs', 'resetBreadcrumbs']),

      ...mapActions('xsell', [
        'getXsellItemsForProduct',
        'getXsellForActiveColorKitSubs'
      ]),

      ...mapActions('waitlist', ['initWaitlists']),
      ...mapActions('colorbar', ['loadLocation']),
      ...mapActions('cart', ['loadCart']),
      ...mapActions('colorAdvisor', ['initColorAdvisor']),
      ...mapActions('systemSettings', ['getGlobalFeaturedProduct']),

      addProductToRecentlyViewed() {
        let storedProducts = null;
        //- When 3rd party cookies are blocked from the browser, sessionStorage is not available
        try {
          storedProducts = window.sessionStorage.getItem('recently-viewed');
        } catch (e) {
          // do nothing
        }

        let recentlyViewed = [];

        if (storedProducts) {
          recentlyViewed = JSON.parse(storedProducts);
        }

        recentlyViewed = [...new Set([...recentlyViewed, this.urlKey])];
        try {
          window.sessionStorage.setItem('recently-viewed', JSON.stringify(recentlyViewed));
        } catch (e) {
          // do nothing
        }
      },

      async initProductSsr() {
        try {
          await this.getProductByUrlKey(this.urlKey);

          this.setBreadcrumbFragments();
        } catch (error) {
          return null;
        }
      },

      async initProduct() {
        try {
          const product = await this.getProductByUrlKey(this.urlKey);

          // document.title = product.meta_title || product.name;
          await this.getXsellItemsForProduct({ productCode: product.code });
          //- check for active subs of they product type only once
          if (!this.subscription.id) {
            await this.getCustomerNextSubByProductType({ productType: product.product_type });
          }

          this.setBreadcrumbFragments();
        } catch (error) {
          return null;
        }
      },

      setBreadcrumbFragments () {
        let fragments = [];

        fragments.push({ title: 'Shop All', url: '/shop-all' });

        if (this.getObjProperty(this, 'product.hierarchy') && Array.isArray(this.product.hierarchy)) {
          const fragmentsWithUrl = this.product.hierarchy.filter(hp => hp.url);
          const mappedFragments = fragmentsWithUrl.map(fragment => {
            return {
              title: fragment.name,
              url: fragment.url
            };
          });

          fragments = fragments.concat(mappedFragments);
        }

        if (this.getObjProperty(this, 'product.name')) {
          fragments.push({ title: this.product.name });
        }

        if (fragments.length) {
          this.setBreadcrumbs(fragments);
        }
      },

      priceModuleDisplayLogic() {
        // Determine if the referer is from google ads
        const urlParams = new URLSearchParams(window.location.search);
        const utmMedium = (urlParams.get('utm_medium') || '').toLowerCase();
        const utmSource = (urlParams.get('utm_source') || '').toLowerCase();
        const utmCampaign = (urlParams.get('utm_campaign') || '').toLowerCase();

        if (utmMedium === 'cpc' && utmSource.includes('google')) {
          this.setShowPriceModule(true);
        }

        if (utmMedium === 'cpc' && utmCampaign.includes('shopping')) {
          this.setGoogleShopping(true);
        }
      },

      loadDYCrossSell(product) {
        if (window.DYO && this.cmsSettings.dyRecommendationStrategyId) {
          let realtimeRules = [{
            id: -1,
            query: {
              conditions: [{
                field: "productType", // Condition
                arguments: [{
                  action: "IS", // Action type IS / IS_NOT / CONTAINS / EQ / GT / GTE / LT / LTE 
                  value: product.product_type_name // Value of condition
                }]
              }]
            },
            type: "exclude", // Include or exclude
            slots: [] // Position in widget
          }];

          window.DYO.recommendationWidgetData(this.cmsSettings.dyRecommendationStrategyId, {
            context: {
              type: 'PRODUCT',
              data: ['' + product.id]
            },
            realtimeRules,
          }, (err, data) => {
            if (err || !data?.slots?.length) {
              return;
            }
            let dyProductIds = [];
            
            data.slots.forEach(slot => {
              dyProductIds.push(+slot.item.sku);
            });

            vueProductSvc.getProductsById({ ids: dyProductIds }).then(res => {
              this.setDYRecommendedProducts(res.data);
            });
          });
        }
      }
    },
  };
</script>
